import './search-results.scss'
import { CmsLinkComponent } from '@cms/components/cmsLink/CmsLink'
import type { CmsLink } from '@cms/interfaces/cmsLink'
import type { Film } from '@vista-digital/ocapi-types/v1'
import {
    CxmEntityType,
    getAllFilms,
    getCxmFilmsForComponent,
    loadAllFilms,
    loadCxmFilmsForComponent,
} from '@vista-digital/occ-domain'
import { Icon, useI18n } from '@vista-digital/occ-ui'
import { sortAndSliceFilms } from '@vista-digital/occ-ui/src/lib/utils/film-sort-utils'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { getRecentCinemas } from '@/content/pages/cinema/helpers'
import type { searchResponse } from '@/content/sections/header/subcomponents/search/subcomponents/searchResponse'
import { cmsDataService } from '@/content/services/cms.data.service'
import { FilmListIdType, gtm } from '@/content/services/gtm'
import { navigationService } from '@/content/services/navigation.service'
import { useUrlService } from '@/content/services/omnia-service-context'

export const SearchResults: FC<{
    response: searchResponse
    onClick: () => void
    searchTerm: string
    loading: boolean
}> = props => {
    const { response } = props
    const {
        searchResultsTitle,
        noResultsMessage,
        topFilmsTitle,
        recentCinemasTitle,
        minimumCharactersRequiredMessage,
    } = cmsDataService.get().searchConfiguration
    const [topFilms, setFilms] = useState<Film[]>([])
    const recentCinemas = getRecentCinemas()
    const i18n = useI18n()
    const urlService = useUrlService()

    const getGtmFilmListIdType = () => {
        switch (navigationService.getCurrentPage()) {
            case 'Home':
                return FilmListIdType.home_page_top_films
            case 'Films':
                return FilmListIdType.films_page_top_films
            default:
                return null
        }
    }

    const getCategoryIconName = (category: string): string => {
        switch (category) {
            case 'film':
                return 'film'
            case 'cinema':
                return 'pin'
            case 'content':
                return 'globe'
            case 'offerPage':
            case 'competitionPage':
                return 'gift'
            default:
                return ''
        }
    }

    // This should probably be refactored into a shared hook used here and in TopFilms.tsx
    useEffect(() => {
        if (response.showEmptyState) {
            void (async () => {
                let films = getAllFilms() || []
                if (!films.length) {
                    await loadAllFilms()
                    films = getAllFilms() || []
                }

                let cxmFilms = getCxmFilmsForComponent('Top Film List') || []
                if (!cxmFilms.length) {
                    await loadCxmFilmsForComponent(CxmEntityType.FilmList, 'Top Film List', 4)
                    cxmFilms = getCxmFilmsForComponent('Top Film List') || []
                }
                const topFilmsData = sortAndSliceFilms(films, i18n, cxmFilms, undefined, 3)
                if (topFilmsData.length) {
                    setFilms([...topFilmsData])
                    const filmIdType = getGtmFilmListIdType()
                    if (filmIdType !== null) gtm.ecommerce.films.viewItemList(topFilmsData, filmIdType, i18n)
                }
            })()
        }
    }, [response.showEmptyState])

    // Convert a url that include protocol and domain to just the path for use as a nav link
    function makeCmsLink(url: string, name: string): CmsLink {
        return {
            target: '',
            url: url,
            name: name,
        }
    }

    if (props?.response.results?.length === 0) {
        if (props.searchTerm.length > 0 && props.searchTerm.length < 3) {
            return (
                <div className="auto-complete_list">
                    <div className="no_result" tabIndex={1}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: minimumCharactersRequiredMessage,
                            }}
                        />
                    </div>
                </div>
            )
        }

        if (props.loading) {
            return null
        }

        if (props.response.showEmptyState) {
            return (
                <div className="auto-complete_list">
                    <div className="empty_state" tabIndex={1}>
                        <p className="label">{topFilmsTitle}</p>
                        <ul>
                            {topFilms.map((film, index) => (
                                <li data-id={index} key={index} onClick={props.onClick} className="film">
                                    <CmsLinkComponent
                                        link={makeCmsLink(
                                            urlService.getFilmDetailsUrl(film),
                                            i18n.translatable(film.title)
                                        )}
                                    >
                                        <div className="film-items">
                                            <span className="icon-container">
                                                <Icon name="film" className="film-icon" />
                                            </span>
                                            {i18n.translatable(film.title)}
                                        </div>
                                    </CmsLinkComponent>
                                </li>
                            ))}
                        </ul>
                        {recentCinemas.length !== 0 && (
                            <>
                                <p className="label">{recentCinemasTitle}</p>
                                <ul>
                                    {recentCinemas.map((cinema, index) => (
                                        <li data-id={index} key={index} onClick={props.onClick} className="cinema">
                                            <span className="icon-container">
                                                <Icon name="pin" className="cinema-icon" />
                                            </span>
                                            <CmsLinkComponent link={makeCmsLink(cinema.url, cinema.title)} />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            )
        } else {
            return (
                <div className="auto-complete_list">
                    <div className="no_result" tabIndex={1}>
                        <div dangerouslySetInnerHTML={{ __html: noResultsMessage }} />
                    </div>
                </div>
            )
        }
    }

    return (
        <div className="auto-complete_list">
            <div className="results">
                <p className="label">{searchResultsTitle}</p>
                <ul>
                    {props.response.results.map((result, index) => (
                        <li
                            data-id={index}
                            key={index}
                            onClick={props.onClick}
                            className={`auto-complete_result ${result.pageAlias}`}
                        >
                            <span className="icon-container">
                                <Icon name={getCategoryIconName(result.category)} />
                            </span>
                            <CmsLinkComponent link={makeCmsLink(result.url, result.title)} />
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
